import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import * as depositActions from '~actions/properties/deposit';
import Header from '~components/property-header';
import DepositList from '~pages/dashboard/properties/deposit-and-fees/deposit-list';
import DepositModal from '~pages/dashboard/properties/deposit-and-fees/deposit-modal';
import { communicationStatus, platformEntity, entityAction } from '~constants';
import { PlusOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import showElementByAuth from '~helpers/auth';
import authControl from '~components/auth-control';
import generatePath from '~settings/routing';
import { isEmptyObject } from '~client/helpers/property-edit';
import classNames from 'classnames';
import DepositLocaleCard from '~pages/dashboard/properties/deposit-and-fees/deposit-locale-card';
import { bookingJourneyType } from 'common/constants/booking';

const mapStateToProps = state => {
  const data = state.dashboard.depositReducer?.toJS();

  return {
    property: data.propertyDeposite || {},
    getPropertyStatus: data.communication.getPropertyDeposite.status,
    deleteStatus: data.communication.delete.status,
    updatePropertyStatus: data.communication.updatePropertyPaymentsEnable.status,
  };
};

const mapDispatchToProps = dispatch => ({
  getPropertyDepositePayment: (slug, successCallback) => {
    dispatch(depositActions.getPropertyDepositePayment(slug, successCallback));
  },
  createDeposit: (data, successCallback) => {
    dispatch(depositActions.createDeposit(data, successCallback));
  },
  updateDeposit: (data, successCallback) => {
    dispatch(depositActions.updateDeposit(data, successCallback));
  },
  deletedDeposit: (id, successCallback) => {
    dispatch(depositActions.deletedDeposit({ id }, successCallback));
  },
  updatePropertyPaymentsEnabled: (data, successCallback) => {
    dispatch(depositActions.updatePropertyPaymentsEnabled(data, successCallback));
  },
});

@connect(mapStateToProps, mapDispatchToProps)
@withTranslation()
@authControl(platformEntity.PAYMENTS_LINE_ITEM_RULES, entityAction.READ, props =>
  generatePath('property.homepage', { propertySlug: props.match.params.propertySlug }),
)
export default class DepositAndFees extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showModal: false,
      modalType: null,
      depositId: null,
      isLoading: true,
      showBookNowModal: false,
      propertySlug: decodeURIComponent(props.match.params.propertySlug),
      currentUpdatePropertyPaymentsEnabled: null,
    };
  }

  componentDidMount() {
    this.props.getPropertyDepositePayment(this.state.propertySlug, () => {
      if (isEmptyObject(this.props.property)) {
        this.props.history.push(generatePath('properties', {}));
      }
    });
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.getPropertyStatus === communicationStatus.IDLE &&
      prevProps.getPropertyStatus === communicationStatus.FETCHING
    ) {
      this.showLoadingModal();
    }
  }

  showLoadingModal = () => {
    this.setState({ isLoading: true });
  };

  closeModal = (type, id) => {
    this.setState({
      showModal: !this.state.showModal,
      modalType: type,
      depositId: id || null,
    });
  };

  enableBookNow = (params = {}, targetLocale) => {
    params.id = this.props.property.id;
    params.paymentProcess = this.props.property.paymentProcess || 'ONLINE';
    if (targetLocale) {
      this.setState({
        currentUpdatePropertyPaymentsEnabled: targetLocale,
      });
    }

    this.props.updatePropertyPaymentsEnabled(params, () => {
      this.props.getPropertyDepositePayment(this.state.propertySlug);
    });

    this.setState({
      isLoading: false,
      showBookNowModal: false,
    });
  };

  handleBookNowModal = bool => {
    this.setState({
      showBookNowModal: bool,
    });
  };

  handleCreateDeposit = params => {
    this.props.createDeposit(params, () => {
      this.props.getPropertyDepositePayment(this.state.propertySlug);
    });
  };

  handleUpdateDeposit = params => {
    this.props.updateDeposit(params, () => {
      this.props.getPropertyDepositePayment(this.state.propertySlug);
    });
  };

  handleDeletedDeposit = id => {
    this.props.deletedDeposit(id, () => {
      this.props.getPropertyDepositePayment(this.state.propertySlug);
    });
  };

  render() {
    const { t, property, getPropertyStatus, updatePropertyStatus } = this.props;
    const totalCount =
      property.paymentDepositRules &&
      property.paymentDepositRules.edges &&
      property.paymentDepositRules.edges.length
        ? property.paymentDepositRules.edges.length
        : 0;

    return (
      <div className="deposit-and-fees">
        <Choose>
          <When condition={getPropertyStatus !== communicationStatus.IDLE && this.state.isLoading}>
            <div className="property-edit__loading">
              <Spin />
            </div>
          </When>
          <Otherwise>
            <Header t={t} property={property} type="deposit-and-fees" />
            <div className="deposit-and-fees__content">
              <If condition={property.bookingJourney !== bookingJourneyType.LEAD_GENERATION}>
                <div className="deposit-and-fees__content-header">
                  <div className="deposit-and-fees__header">
                    <DepositLocaleCard
                      locale="CN"
                      t={t}
                      showBookNowModal={this.state.showBookNowModal}
                      onClickEnableBookNow={v => {
                        this.enableBookNow({ enableBookNowOnCn: v }, 'CN');
                      }}
                      enableBookNowLoading={
                        (updatePropertyStatus === communicationStatus.FETCHING ||
                          getPropertyStatus === communicationStatus.FETCHING) &&
                        this.state.currentUpdatePropertyPaymentsEnabled === 'CN'
                      }
                      isUserHaveUpgradePermission={showElementByAuth(
                        platformEntity.PROPERTIES_PROPERTIES,
                        entityAction.UPDATE,
                      )}
                      handleBookNowModal={this.handleBookNowModal}
                      enableBookNow={property.acceptsCnDepositPayment}
                      property={property}
                    />
                    <DepositLocaleCard
                      locale="ROW"
                      t={t}
                      showBookNowModal={this.state.showBookNowModal}
                      onClickEnableBookNow={v => {
                        this.enableBookNow({ enableBookNowOnRow: v }, 'ROW');
                      }}
                      enableBookNowLoading={
                        (updatePropertyStatus === communicationStatus.FETCHING ||
                          getPropertyStatus === communicationStatus.FETCHING) &&
                        this.state.currentUpdatePropertyPaymentsEnabled === 'ROW'
                      }
                      isUserHaveUpgradePermission={showElementByAuth(
                        platformEntity.PROPERTIES_PROPERTIES,
                        entityAction.UPDATE,
                      )}
                      handleBookNowModal={this.handleBookNowModal}
                      enableBookNow={property.acceptsRowDepositPayment}
                      property={property}
                    />
                  </div>
                </div>
              </If>

              <div
                className={classNames('deposit-and-fees__list', {
                  'deposit-and-fees__list-empaty': totalCount <= 0,
                })}
              >
                <If condition={property}>
                  <div className="deposit-and-fees__list__header">
                    <span>Deposit & Fees Card</span>
                    <If
                      condition={
                        totalCount > 0 &&
                        showElementByAuth(
                          platformEntity.PAYMENTS_LINE_ITEM_RULES,
                          entityAction.CREATE,
                        )
                      }
                    >
                      <button
                        className="deposit-and-fees__list-add-new"
                        onClick={() => {
                          this.closeModal('add');
                        }}
                      >
                        <PlusOutlined />
                        <span className="deposit-and-fees__add-new-text">
                          {t('cms.deposit_and_fees.content_ttl.deposit_card.add_new_fee')}
                        </span>
                      </button>
                    </If>
                  </div>

                  <If condition={property.paymentDepositRules}>
                    <DepositList
                      t={t}
                      paymentDepositRules={property.paymentDepositRules}
                      onClose={this.closeModal}
                      deletedDeposit={this.handleDeletedDeposit}
                      billingCycle={property && property.billingCycle ? property.billingCycle : ''}
                      currency={property && property.currency ? property.currency : ''}
                      deleteStatus={this.props.deleteStatus}
                    />
                  </If>
                </If>
              </div>
            </div>
            <If condition={this.state.showModal}>
              <DepositModal
                t={t}
                activeModal
                property={property}
                onClose={this.closeModal}
                createDeposit={this.handleCreateDeposit}
                updateDeposit={this.handleUpdateDeposit}
                deletedDeposit={this.handleDeletedDeposit}
                modalType={this.state.modalType}
                depositId={this.state.depositId}
                isLast={totalCount === 1}
                acceptsDepositPayment={property.acceptsDepositPayment}
              />
            </If>
          </Otherwise>
        </Choose>
      </div>
    );
  }
}

DepositAndFees.propTypes = {
  t: PropTypes.func.isRequired,
  getPropertyDepositePayment: PropTypes.func,
  property: PropTypes.object,
  getPropertyStatus: PropTypes.string,
  match: PropTypes.object,
  updatePropertyStatus: PropTypes.string,
  deleteStatus: PropTypes.string,
  createDeposit: PropTypes.func,
  updateDeposit: PropTypes.func,
  deletedDeposit: PropTypes.func,
  updatePropertyPaymentsEnabled: PropTypes.func,
  history: PropTypes.object,
};

DepositAndFees.defaultProps = {
  t: () => {},
  getPropertyDepositePayment: () => {},
  property: {},
  getPropertyStatus: '',
  match: {},
  updatePropertyStatus: '',
  deleteStatus: '',
  createDeposit: () => {},
  updateDeposit: () => {},
  deletedDeposit: () => {},
  updatePropertyPaymentsEnabled: () => {},
  history: {},
};
